import type { Fraction } from '@mimir/common/util/fractionType';
import type { VideoApiActions } from '@mimir/client-common/util/apiActions';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { PlayableItemDto, PlayableItemGetOptions, VirtualClipInfo } from '@mimir/common/dto/playableItemDto';
import type { SegmentProvider, SegmentInfo } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import type { EncodedData } from '@mimir/common/mpegts/demuxerTypes';
import { addFractions, approximateFractionValue, compareFractions, divideFractions, multiplyFractions } from '@mimir/common/util/integerMath';
import { frac } from '@mimir/web/util/clientMath';
import { ItemSegmentUrlCache } from '@mimir/client-common/util/itemUrlCache';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import type { Logger } from '@mimir/common/types/logger';
import { readWholeStream } from '@mimir/common/util/readableStream';
import { demuxTsSegment } from '@mimir/common/mpegts/mpegTsDemuxer';
import { invariant } from '@mimir/common/util/invariant';
import { decodeFmp4InitSegment, demuxFmp4Segment } from '@mimir/common/mp4/fmp4Demuxer';
import type { Mp4Info } from '@mimir/common/mp4/mp4Types';
import { fetchAndInspectMoov } from '@mimir/common/mp4/mp4Demuxer';
import { buildEncodedDataFromVirtualSegment, findVirtualSegmentIndex } from '@mimir/common/mp4/virtualSegments';
import { fetchByteArrays } from '@mimir/common/mp4/fetchByteArrays';
import { Piggyback, TaskTracking } from '@mimir/common/async/taskTracking';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';
import type { SegmentedMediaInfo } from '@mimir/common/dto/pollingDtos';

export function createMimirSegmentProvider(
  services: {
    apiActions: VideoApiActions,
    fetch: BasicFetch,
    logger: Logger,
    itemPollingOps: ItemPollingOps,
  },
  itemId: string,
  ignoreVideo: boolean,
  ignoreAudio: boolean,
): SegmentProvider {
  return new DelegatedSegmentProvider(services, itemId, ignoreVideo, ignoreAudio);
}


function createMimirSegmentProviderInternal(
  services: {
    apiActions: VideoApiActions,
    fetch: BasicFetch,
    logger: Logger,
    itemPollingOps: ItemPollingOps,
  },
  itemId: string,
): SegmentProvider {
  const { apiActions, fetch, logger } = services;

  const options : PlayableItemGetOptions = {
    useEditProxy: true,
    streamSetPreference: [
      [StreamRoleId.LiveRecordWebproxyTs],
      [StreamRoleId.LiveRecordAudioOnlyProxyTs],
      [StreamRoleId.LiveRecordAudioOnlyHighresTs],
      [StreamRoleId.LiveRecordHighresTs],
      [StreamRoleId.EditProxyFull],
    ],
    peakOffset: undefined,
  };

  let closed = false;
  let cachedItem : PlayableItemDto | null = null;
  let itemSegmentCache: ItemSegmentUrlCache | undefined;
  let format: 'ts' | 'fmp4' | 'flatEditProxy' | 'flatWebProxy' | 'flatHighRes' | undefined;
  let mp4Info: Mp4Info | undefined;
  const onDurationChangedListeners : ((duration: Fraction) => void)[] = [];

  let segmentedMedia: Partial<Record<StreamRoleId, SegmentedMediaInfo>> = {};
  let lastDuration: Fraction | null = null;

  const pollListenerId = services.itemPollingOps.addPollListener(itemId, ['playableDuration', 'segmentedMedia'], response => {
    if (response === null) {
      return;
    }

    segmentedMedia = response.segmentedMedia;

    if (response.playableDuration !== null) {
      const playableDuration = response.playableDuration;
      if (lastDuration === null || compareFractions(lastDuration, playableDuration) !== 0) {
        lastDuration = playableDuration;
        onDurationChangedListeners.forEach((l) => l(playableDuration));
      }
    }

  });

  const getItem = async (refresh: boolean): Promise<PlayableItemDto> => {
    const initialFetch = (cachedItem === null);
    if (initialFetch || refresh) {
      cachedItem = await apiActions.getPlayableItem(itemId, options);
      if (cachedItem === null) {
        throw Error('Failed to get playable item');
      }

      return cachedItem;
    }
    if (cachedItem === null) {
      throw Error('Failed to get playable item');
    }
    return cachedItem;
  };

  return {
    async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
      const item = await getItem(false);

      if (item.playableArtifact?.type === 'flatEditProxy'
        // If we have a web proxy for a stereo input that was created without any downmixing,
        // we can use it as a single flat file, so we treat it as an edit proxy
        || (item.playableArtifact?.type === 'flatWebProxy' && item.proxyAudioTrackUrls.length === 2 && item.webProxyAudioDownMixInfo === null)
      ) {
        format = 'flatEditProxy';
        const flatEditProxyUrl = item.playableArtifact.url;
        if (mp4Info === undefined) {
          mp4Info = await fetchAndInspectMoov(fetch, flatEditProxyUrl, undefined, false, false);
        }

        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.playableArtifact?.type === 'flatWebProxy') {
        format = 'flatWebProxy';
        const flatWebProxyUrl = item.playableArtifact.url;
        if (mp4Info === undefined) {
          mp4Info = item.itemType === 'video' ? await fetchAndInspectMoov(fetch, flatWebProxyUrl, undefined, false, true) : undefined;

          for (let i = 0; i < item.proxyAudioTrackUrls.length; i++) {
            const proxyAudioTrackUrl = item.proxyAudioTrackUrls[i];
            mp4Info = await fetchAndInspectMoov(fetch, proxyAudioTrackUrl, mp4Info, true, false);
          }
        }

        if (mp4Info === undefined) {
          throw Error('Item has no media');
        }
        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.playableArtifact?.type === 'flatHighRes') {
        format = 'flatHighRes';
        const flatHighresUrl = item.playableArtifact.url;
        if (mp4Info === undefined) {
          mp4Info = item.itemType === 'video' ? await fetchAndInspectMoov(fetch, flatHighresUrl, undefined, false, false) : undefined;
        }

        if (mp4Info === undefined) {
          throw Error('Item has no media');
        }

        const virtualSegmentIndex = findVirtualSegmentIndex(position, mp4Info.virtualSegments);
        if (virtualSegmentIndex === -1) {
          return null;
        }
        const virtualSegment = mp4Info.virtualSegments[virtualSegmentIndex];
        return {
          id: `${virtualSegmentIndex}`,
          start: virtualSegment.inclusiveStartInSeconds,
          end: virtualSegment.exclusiveEndInSeconds,
        };
      }

      if (item.media === null) {
        throw Error('Item has no media');
      }

      if (item.playableArtifact?.type !== 'stream' || item.playableArtifact.streamSet.length === 0) {
        throw new Error('Item has unsupported media');
      }

      format = item.playableArtifact.streamSet[0] === StreamRoleId.EditProxyFull ? 'fmp4' : 'ts';
      itemSegmentCache = new ItemSegmentUrlCache(
        apiActions,
        item.id,
        'get',
        item.playableArtifact.streamSet[0],
        10,
      );
      const segmentDuration = item.media.segmentDuration;

      const segmentIndex =
          Math.floor(approximateFractionValue(divideFractions(position, segmentDuration)));

      const segmentCount = segmentedMedia[item.playableArtifact.streamSet[0]]?.segmentCount ?? item.media.segmentCount;
      if (segmentIndex < 0 || segmentIndex >= segmentCount) {
        return null;
      }

      const segmentIndexAsFraction = frac(segmentIndex, 1);

      return {
        id: (segmentIndex + (format === 'fmp4' ? 1 : 0)).toString(),
        start: multiplyFractions(segmentIndexAsFraction, segmentDuration),
        end: addFractions(multiplyFractions(segmentIndexAsFraction, segmentDuration), segmentDuration),
      };
    },
    async getSegment(id: string): Promise<EncodedData> {
      const segmentIndex = parseInt(id);
      if (segmentIndex.toString() !== id) {
        throw new Error(`Bad segment id: ${id}`);
      }

      if (format === 'flatEditProxy' || format === 'flatWebProxy' || format === 'flatHighRes') {
        invariant(mp4Info !== undefined, 'Should have fetched mp4 info');
        const virtualSegment = mp4Info.virtualSegments[segmentIndex];
        const byteArrays = await fetchByteArrays(fetch, virtualSegment);
        return buildEncodedDataFromVirtualSegment(byteArrays, virtualSegment, mp4Info);
      }

      invariant(itemSegmentCache !== undefined && format !== undefined, () => `Should have invoked getSegmentInfoAt() because asking for segment ${id}`);
      if (format === 'fmp4' && mp4Info === undefined) {
        const url = await itemSegmentCache.getMediaSegmentUrl(0);
        logger.info('MimirSegmentProvider: fetching init fmp4 segment');
        const response = await fetch(url, { });

        if (!response.ok || (response.body === null)) {
          throw new Error(`Failed to fetch init segment: ${response.status} ${response.statusText}`);
        }
        const body = response.body;
        const fmp4InitSegmentBytes = await readWholeStream(body);
        mp4Info = decodeFmp4InitSegment(fmp4InitSegmentBytes, false, false);
      }

      const url = await itemSegmentCache.getMediaSegmentUrl(segmentIndex);

      logger.info(`MimirSegmentProvider: fetching segment: ${segmentIndex}`);

      const response = await fetch(url, { });

      if (!response.ok || (response.body === null)) {
        throw new Error(`Failed to fetch segment: ${response.status} ${response.statusText}`);
      }

      const body = response.body;
      const bytes = await readWholeStream(body);
      return mp4Info === undefined
        ? demuxAndFilterTsSegment(bytes, false, false)
        : demuxFmp4Segment(logger, mp4Info, bytes);
    },
    async getDuration(): Promise<Fraction> {
      const item = await getItem(true);
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      if (!item.playableDuration) {
        throw Error('Item does not have duration');
      }
      return item.playableDuration;
    },
    async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
      const item = await getItem(false); // getDuration() always runs first and does getItem(true), populating the cache
      if (item === null) {
        throw Error('Failed to get playable item');
      }
      return item.virtualClipInfo;
    },
    close(): void {
      if (closed) {
        throw new Error('MimirSegmentProvider already closed');
      }
      closed = true;
      services.itemPollingOps.removePollListener(pollListenerId);
    },
    addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
      onDurationChangedListeners.push(onDurationChanged);
      return () => {
        const index = onDurationChangedListeners.indexOf(onDurationChanged);
        if (index !== -1) {
          onDurationChangedListeners.splice(index, 1);
        }
      };
    }
  };
}

function demuxAndFilterTsSegment(bytes: Uint8Array, ignoreVideo: boolean, ignoreAudio: boolean) {
  const data = demuxTsSegment(bytes, false);
  if (ignoreVideo) {
    data.encodedVideoData = undefined;
  }
  if (ignoreAudio) {
    data.encodedAudioData = [];
  }

  return data;
}


class DelegatedSegmentProvider implements SegmentProvider {

  static segmentProviders: Record<string, CachedSegmentProvider> = {};

  private cached: CachedSegmentProvider;
  private durationListeners: ((duration: Fraction) => void)[] = [];
  private innerDurationListenerRemoval: () => void;

  constructor(
    services: {
      apiActions: VideoApiActions,
      fetch: BasicFetch,
      logger: Logger,
      itemPollingOps: ItemPollingOps,
    },
    private itemId: string,
    private ignoreVideo: boolean,
    private ignoreAudio: boolean,
  ) {
    let cached: CachedSegmentProvider | undefined = DelegatedSegmentProvider.segmentProviders[itemId];
    if (!cached) {
      cached = {
        nReferences: 0,
        innerProvider: createMimirSegmentProviderInternal(services, itemId),
        taskTrackingGetSegmentInfoAt: new TaskTracking(),
        taskTrackingGetSegment: new TaskTracking(),
        piggyBackGetDuration: new Piggyback(),
        piggyBackGetVirtualClipInfo: new Piggyback(),
      };
      DelegatedSegmentProvider.segmentProviders[itemId] = cached;
    }
    this.cached = cached;
    this.cached.nReferences++;
    this.innerDurationListenerRemoval = this.cached.innerProvider.addDurationChangedListener(duration => this.durationListeners.forEach(l => l(duration)));
  }

  async getSegmentInfoAt(position: Fraction): Promise<SegmentInfo | null> {
    const id = `${position.numerator}:${position.denominator}`;
    const info = await this.cached.taskTrackingGetSegmentInfoAt.runOrPiggyback(id, () => this.cached.innerProvider.getSegmentInfoAt(position));
    return info;
  }

  async getSegment(id: string): Promise<EncodedData> {
    const data = await this.cached.taskTrackingGetSegment.runOrPiggyback(id, () => this.cached.innerProvider.getSegment(id));

    return {
      encodedVideoData: this.ignoreVideo ? undefined : data.encodedVideoData,
      encodedAudioData: this.ignoreAudio ? [] : data.encodedAudioData,
    };
  }

  async getDuration(): Promise<Fraction> {
    return await this.cached.piggyBackGetDuration.runOrPiggyback(() => this.cached.innerProvider.getDuration());
  }

  async getVirtualClipInfo(): Promise<VirtualClipInfo | null> {
    return await this.cached.piggyBackGetVirtualClipInfo.runOrPiggyback(() => this.cached.innerProvider.getVirtualClipInfo());
  }

  addDurationChangedListener(onDurationChanged: (duration: Fraction) => void): () => void {
    this.durationListeners.push(onDurationChanged);
    return () => {
      const index = this.durationListeners.indexOf(onDurationChanged);
      if (index !== -1) {
        this.durationListeners.splice(index, 1);
      }
    };
  }

  close(): void {
    this.cached.nReferences--;
    this.innerDurationListenerRemoval();
    this.durationListeners.splice(0);

    // When dropping an item onto the timeline, a source is created
    // as part of the ongoing drag operation. When the operation is completed,
    // the timeline model converts the drag operation into an undo stack operation
    // by undoing the temporary operation (which would remove the source) and
    // executing the operation that is pushed on the undo stack (which would re-add
    // the source). Since the completion of a drag operation is a synchronous
    // operation, the remove source and add source events are going to be triggered
    // within the same event loop iteration. As a consequence, using a setTimeout(0)
    // to defer the actual destruction of the inner provider to the next tick allows
    // the second add source event to re-use the existing provider instead of destroying
    // it and re-creating a new one immediately
    setTimeout(() => {
      // Since several sources can reuse the same provider, they can trigger
      // simultaneous cleanup if they are all removed as part of the same timeline
      // operation. So let's check DelegatedSegmentProvider.segmentProviders directly
      // instead of looking at this.cached in order to avoid double-free problems in
      // case another close() call has already triggered the cleanup
      const cleanupCandidate = DelegatedSegmentProvider.segmentProviders[this.itemId];
      if (cleanupCandidate !== undefined && cleanupCandidate.nReferences === 0) {
        cleanupCandidate.innerProvider.close();
        delete DelegatedSegmentProvider.segmentProviders[this.itemId];
      }
    }, 0);
  }

}


interface CachedSegmentProvider {
  innerProvider: SegmentProvider;
  nReferences: number;
  taskTrackingGetSegmentInfoAt: TaskTracking<SegmentInfo | null>;
  taskTrackingGetSegment: TaskTracking<EncodedData>;
  piggyBackGetDuration: Piggyback<Fraction>;
  piggyBackGetVirtualClipInfo: Piggyback<VirtualClipInfo | null>;
}
